import { motion } from "motion/react";
import {
  useId,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type PointerEvent,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";

import { AriaLabelForContext } from "@/contexts/AriaLabelForContext";

import { ActionBox } from "./ActionBox";
import { Icon } from "./Icon";
import { Subtitle } from "./Subtitle";

export function Card({
  children,
  "aria-labelledby": ariaLabelledBy,
  className,
  ...props
}: PropsWithChildren<{ label?: string } & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>) {
  const defaultLabelId = useId();
  return (
    <AriaLabelForContext value={ariaLabelledBy ?? defaultLabelId}>
      <section
        className={twMerge(
          "relative origin-bottom overflow-hidden rounded-sm bg-white shadow-lg transition-all",
          className,
        )}
        aria-labelledby={ariaLabelledBy ?? defaultLabelId}
        {...props}
      >
        {children}
      </section>
    </AriaLabelForContext>
  );
}

export function CardSubtitleHeader({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <CardHeader className={className}>
      <CardTitle>
        <Subtitle>{children}</Subtitle>
      </CardTitle>
    </CardHeader>
  );
}

export function CardActionsHeader({ title, children, id }: PropsWithChildren<{ title: ReactNode; id?: string }>) {
  return (
    <CardHeader>
      <CardTitle>
        <Subtitle id={id}>{title}</Subtitle>
      </CardTitle>
      <ActionBox className="min-h-12 py-2">{children}</ActionBox>
    </CardHeader>
  );
}

export function CardStack({
  children,
  className,
  ...props
}: PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>) {
  return (
    <div className={twMerge("relative flex flex-col", className)} {...props}>
      {children}
    </div>
  );
}

export function CardTabs({
  children,
  className,
  ...props
}: PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>) {
  return (
    <div className={twMerge("flex flex-row gap-1 px-2", className)} {...props}>
      {children}
    </div>
  );
}

export function CardTab({
  children,
  active = false,
  className,
  ...props
}: { active?: boolean } & PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLButtonElement>, HTMLButtonElement>>) {
  return (
    <button
      type="button"
      className={twMerge(
        "relative flex origin-bottom cursor-pointer items-center justify-between rounded-t-sm bg-white p-2 px-4 font-interest shadow-md transition-[padding,margin] hover:-mt-1 hover:pb-3",
        active ? "-mt-1 pb-3" : "text-placeholder",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export const MotionCard = motion.create(Card);

export function CardHeader({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={twMerge("flex min-w-0 flex-row items-start justify-between gap-2 overflow-hidden px-2", className)}>
      {children}
    </div>
  );
}

export function CardTitle({ children }: PropsWithChildren) {
  return <div className="mr-auto flex min-w-0 flex-1 flex-col py-4 first:pl-2">{children}</div>;
}

export function CardTextContent({ className, children }: PropsWithChildren<{ className?: string }>) {
  return <div className={twMerge("px-4 pb-4", className)}>{children}</div>;
}

export function CardExpand({ onClick }: { onClick?: (event: PointerEvent<HTMLButtonElement>) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center justify-center bg-linear-to-t from-black/10 to-transparent py-2 transition-colors duration-10 hover:from-black/15"
    >
      <Icon name="chevron-down" />
    </button>
  );
}
